import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useWorkspaceStore } from '@/store/workspaceStore'
import { ProjectExplorer } from '../left/ProjectExplorer'
import { ComponentLibrary } from '../left/ComponentLibrary'
import { FolderTree, Search } from 'lucide-react'
import type { ActivityWithExchanges } from '@/lib/types/project-graph'
import type { ToolCallEvent } from '@/hooks/workspace/useWorkspaceSocket'

interface LeftPanelProps {
  activities: ActivityWithExchanges[]
  selectedProjectId: string | null
  projectDatabase?: string
  onProjectSelect: (projectId: string) => void
  isLoadingProject?: boolean
  onActivityCreated?: () => void
  onCollapse: () => void
  /** WS events — forwarded to ProjectExplorer for live list refresh. */
  wsEvents?: ToolCallEvent[]
}

export function LeftPanel({
  activities,
  selectedProjectId,
  projectDatabase,
  onProjectSelect,
  isLoadingProject,
  onActivityCreated,
  onCollapse,
  wsEvents,
}: LeftPanelProps) {
  const { leftTab, setLeftTab } = useWorkspaceStore()

  return (
    <div className="h-full flex flex-col bg-background border-r border-border min-h-0">
      <Tabs
        value={leftTab}
        onValueChange={(v) => setLeftTab(v as 'explorer' | 'library')}
        className="flex-1 flex flex-col min-h-0"
      >
        <div className="flex items-center justify-between px-2 py-1.5 border-b border-border bg-surface-sunken/50">
          <TabsList className="h-8 p-0.5 bg-muted/50">
            <TabsTrigger
              value="explorer"
              className="h-7 px-2.5 text-xs gap-1.5 data-[state=active]:bg-background"
            >
              <FolderTree className="h-3.5 w-3.5" />
              Explorer
            </TabsTrigger>
            <TabsTrigger
              value="library"
              className="h-7 px-2.5 text-xs gap-1.5 data-[state=active]:bg-background"
            >
              <Search className="h-3.5 w-3.5" />
              Library
            </TabsTrigger>
          </TabsList>

          <button
            onClick={onCollapse}
            className="p-1 hover:bg-accent rounded-sm transition-colors"
            title="Collapse panel"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M11 19l-7-7 7-7M18 19l-7-7 7-7" />
            </svg>
          </button>
        </div>

        <TabsContent value="explorer" className="flex-1 m-0 overflow-hidden min-h-0">
          <ProjectExplorer
            activities={activities}
            selectedProjectId={selectedProjectId}
            onProjectSelect={onProjectSelect}
            isLoadingProject={isLoadingProject}
            onActivityCreated={onActivityCreated}
            wsEvents={wsEvents}
          />
        </TabsContent>

        <TabsContent value="library" className="flex-1 m-0 overflow-hidden min-h-0">
          <ComponentLibrary
            projectDatabase={projectDatabase}
            activities={activities}
            onExchangeCreated={onActivityCreated}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
